import { ClickAwayListener } from "@nova/component-click-away-listener"
import classNames from "classnames"
import { animationClass } from "features/data-management/workspace/constants"
import { HTMLAttributes, KeyboardEvent, useCallback, useEffect, useRef, useState } from "react"
import * as Icons from "@nova/component-icons"

export const LayersSearchBar = ({
    searchContents,
    setSearchContents,
    ...rest
}: {
    searchContents: string
    setSearchContents: (contents: string) => void
} & HTMLAttributes<HTMLDivElement>) => {
    const searchRef = useRef<HTMLInputElement>(null)
    const [searchBarIsActive, setSearchBarIsActive] = useState(false)
    const [isHoveringSearchBar, setIsHoveringSearchBar] = useState(false)

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === "Enter" && searchBarIsActive) {
                e.stopPropagation()
                setSearchBarIsActive(false)
            } else if (e.key === "Escape" && searchBarIsActive) {
                e.stopPropagation()
                setSearchBarIsActive(false)
                setSearchContents("")
            }
        },
        [searchBarIsActive, setSearchContents]
    )

    useEffect(() => {
        if (searchBarIsActive) searchRef.current?.focus()
    }, [searchBarIsActive])

    useEffect(() => {
        if (searchContents === undefined) {
            setSearchBarIsActive(false)
            searchRef.current?.blur()
        }
    }, [searchContents])

    const hasContents = searchContents.length > 0

    return (
        <ClickAwayListener onClickAway={() => setSearchBarIsActive(false)}>
            <div
                {...rest}
                className={classNames(
                    "h-8 py-1.5 pl-2.5 flex gap-2 items-center rounded-lg cursor-text border relative",
                    animationClass,
                    {
                        "pr-3": !hasContents && !searchBarIsActive,
                        "pr-1.5 text-gray-0": hasContents || searchBarIsActive,
                        "border-gray-500": !searchBarIsActive,
                        "border-blue-200": searchBarIsActive,
                        "text-gray-300": !isHoveringSearchBar && !searchBarIsActive && !hasContents,
                        "text-gray-100": isHoveringSearchBar && !searchBarIsActive && !hasContents,
                    },
                    rest.className
                )}
                onMouseEnter={() => setIsHoveringSearchBar(true)}
                onMouseLeave={() => setIsHoveringSearchBar(false)}
                onClick={() => setSearchBarIsActive(true)}
            >
                {/* Magnifying Glass */}
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <Icons.SmallIcon
                        Icon={Icons.MagnifyingGlass}
                        className={classNames({
                            "text-gray-300": searchBarIsActive || hasContents,
                        })}
                    />
                </div>

                {/* Text Input or Placeholder */}
                {!searchBarIsActive ? (
                    <h5 className="flex-grow min-w-0 overflow-hidden whitespace-nowrap text-ellipsis select-none cursor-text">
                        {hasContents ? searchContents : "Search for a layer"}
                    </h5>
                ) : (
                    <input
                        className={classNames(
                            "p-0 bg-transparent border-0 !shadow-none min-w-0 flex-grow",
                            "focus:outline-none focus:border-transparent focus:ring-0",
                            "text-sm" // <small> text style
                        )}
                        ref={searchRef}
                        type="text"
                        value={searchContents}
                        onChange={(e) => {
                            const sanitizedContents = e.target.value.trimStart()
                            setSearchContents(sanitizedContents)
                        }}
                        onKeyDown={handleKeyDown}
                    />
                )}

                {/* Clear-All Button */}
                {hasContents && (
                    <div
                        className={classNames(
                            "flex-shrink-0 rounded-full text-gray-100 cursor-pointer w-5 h-5 flex items-center justify-center",
                            "hover:bg-gray-700 hover:text-gray-0",
                            animationClass
                        )}
                        onClick={(e) => {
                            e.stopPropagation()
                            setSearchContents("")
                        }}
                    >
                        <div className="absolute">
                            <Icons.ExtraSmallIcon Icon={Icons.XClose} />
                        </div>
                    </div>
                )}
            </div>
        </ClickAwayListener>
    )
}
